import { PageHeader } from "@finitless/shared/components";
import { Button } from "../../../ui/UIElements/Button";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../../ui/UIElements/Icon";
import {
  useCartRecoveryRuleDetail,
  useCartRecoveryChannels,
  useUpdateCartRecoveryChannel,
  useDeleteCartRecoveryRule,
} from "../../../../hooks/useCartRecovery";
import DeleteConfirmationModal from "../../../ui/Modals/DeleteConfirmationModal";
import CartRecoveryForm from "./CartRecoveryForm";
import { stripHtml } from "../../../../constants/campaignConstants";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { Switch } from "../../../ui/UIElements/Switch";
import "./cart-recovery-ordering.css";

const getPrimaryDelayMinutes = (rule) => {
  const times = rule?.times;
  if (!Array.isArray(times) || times.length === 0) return null;
  const first = times[0];
  return typeof first === "number" ? first : null;
};

const getChannelIconName = (channel) => {
  const c = (channel || "").toLowerCase();
  if (c === "email") return "Mail";
  if (c === "sms") return "MessageSquare";
  if (c === "notification") return "Bell";
  if (c === "webhook") return "Webhook";
  return "Send";
};

const DEFAULT_EMAIL_BODY = "<p><br></p>";

const buildChannelUpdatePayload = (ch, enabled) => {
  const channel = (ch.channel || "email").toLowerCase();
  const body =
    channel === "email"
      ? ch.body || DEFAULT_EMAIL_BODY
      : stripHtml(ch.body || "").trim();
  return {
    enabled,
    channel,
    body,
    title: (ch.title || "").trim(),
  };
};

/**
 * Email device preview — matches CampaignDetailPage structure (campaigns detail preview).
 * No CTA button in cart recovery preview.
 */
const EmailCampaignStylePreview = ({ title, bodyText, tCamp, t }) => {
  return (
    <div className="ordering-generated-preview w-full max-w-[280px]">
      <div className="rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2">
        <div className="relative">
          <div className="absolute top-0 left-1/2 z-10 flex h-6 w-20 -translate-x-1/2 transform items-center justify-center rounded-b-xl bg-gray-950">
            <div className="h-2 w-2 rounded-full bg-gray-800" />
            <div className="ml-1 h-2 w-2 rounded-full bg-gray-800" />
          </div>
          <div className="overflow-hidden rounded-[2rem] bg-white">
            <div className="flex items-center justify-between bg-gray-100 px-4 py-2 text-xs text-gray-600">
              <span>9:41</span>
              <div className="flex items-center gap-1">
                <div className="h-2 w-4 rounded-sm bg-gray-400" />
                <div className="h-2 w-4 rounded-sm bg-gray-400" />
              </div>
            </div>
            <div className="flex items-center justify-between bg-purple-600 px-4 py-3">
              <Icon name="ArrowLeft" className="h-5 w-5 text-white" />
              <span className="font-medium text-white">
                {tCamp("detail.preview.inbox")}
              </span>
              <Icon name="Search" className="h-5 w-5 text-white" />
            </div>
            <div className="min-h-[500px] p-4">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                  <span className="text-sm font-bold text-purple-600">
                    {t("detail.preview.defaultSenderInitial")}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {t("detail.preview.defaultSender")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {tCamp("detail.preview.toMe")}
                  </p>
                </div>
                <span className="text-xs text-gray-400">
                  {tCamp("detail.preview.now")}
                </span>
              </div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                {title || tCamp("detail.preview.defaultSubject")}
              </h3>
              <div className="space-y-2 text-xs text-gray-600">
                {bodyText ? (
                  <p className="whitespace-pre-wrap">
                    {bodyText.substring(0, 200)}
                    {bodyText.length > 200 ? "..." : ""}
                  </p>
                ) : (
                  <>
                    <p>{tCamp("detail.preview.fallbackBodyLine1")}</p>
                    <p>{tCamp("detail.preview.fallbackBodyLine2")}</p>
                    <p>{tCamp("detail.preview.fallbackBodyLine3")}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SmsDevicePreview = ({ title, bodyText, t }) => (
  <div className="ordering-generated-preview w-full max-w-[280px]">
    <div className="rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2">
      <div className="relative">
        <div className="absolute top-0 left-1/2 z-10 flex h-6 w-20 -translate-x-1/2 transform items-center justify-center rounded-b-xl bg-gray-950">
          <div className="h-2 w-2 rounded-full bg-gray-800" />
          <div className="ml-1 h-2 w-2 rounded-full bg-gray-800" />
        </div>
        <div className="flex min-h-[520px] flex-col overflow-hidden rounded-[2rem] bg-[#1c1c1e]">
          <div className="flex items-center justify-between bg-black/40 px-4 py-2 text-[10px] text-gray-400">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <div className="h-2 w-3 rounded-sm bg-gray-500" />
              <div className="h-2 w-4 rounded-sm bg-gray-500" />
            </div>
          </div>
          <div className="flex items-center justify-between border-b border-gray-800/80 px-4 py-3">
            <Icon name="ChevronLeft" className="h-5 w-5 text-blue-400" />
            <div className="text-center">
              <p className="text-xs font-semibold text-white">
                {t("detail.preview.smsPlaceholder")}
              </p>
              <p className="max-w-[140px] truncate text-[10px] text-gray-500">
                {title || "—"}
              </p>
            </div>
            <Icon name="MoreVertical" className="h-5 w-5 text-gray-500" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col px-3 py-4">
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-gray-700 px-3 py-2">
                <p className="text-[11px] font-medium text-gray-200">
                  {title || "—"}
                </p>
                <p className="mt-1 text-[11px] leading-relaxed whitespace-pre-wrap text-gray-100">
                  {bodyText || ""}
                </p>
              </div>
            </div>
            <div className="flex-1" aria-hidden="true" />
          </div>
          <div className="flex justify-center pt-1 pb-3">
            <div className="h-1 w-28 rounded-full bg-gray-600/80" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const NotificationDevicePreview = ({ title, bodyText, t }) => (
  <div className="ordering-generated-preview w-full max-w-[280px]">
    <div className="rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2">
      <div className="relative">
        <div className="absolute top-0 left-1/2 z-10 flex h-6 w-20 -translate-x-1/2 transform items-center justify-center rounded-b-xl bg-gray-950">
          <div className="h-2 w-2 rounded-full bg-gray-800" />
          <div className="ml-1 h-2 w-2 rounded-full bg-gray-800" />
        </div>
        <div className="flex min-h-[520px] flex-col overflow-hidden rounded-[2rem] bg-gradient-to-b from-slate-900 via-indigo-950 to-black">
          <div className="flex items-center justify-between px-4 pt-3 pb-2 text-[10px] text-white/80">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <div className="h-2 w-3 rounded-sm bg-white/30" />
              <div className="h-2 w-4 rounded-sm bg-white/30" />
            </div>
          </div>
          <div className="px-4 pb-2 text-center">
            <p className="text-5xl font-extralight tracking-tight text-white/90">
              9:41
            </p>
          </div>
          <div className="flex min-h-0 flex-1 flex-col px-3 pt-4">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-3 shadow-lg backdrop-blur-md">
              <div className="flex gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-500">
                  <span className="text-xs font-bold text-white">
                    {t("detail.preview.defaultSenderInitial")}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-white">
                      {t("detail.preview.notificationApp")}
                    </p>
                    <span className="shrink-0 text-[10px] text-white/60">
                      {t("detail.preview.notificationNow")}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs font-medium text-white">
                    {title || "—"}
                  </p>
                  <p className="mt-1 text-[11px] leading-snug text-white/85">
                    {bodyText || ""}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1" aria-hidden="true" />
          </div>
          <div className="flex justify-center pt-2 pb-3">
            <div className="h-1 w-28 rounded-full bg-white/20" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SelectedChannelDevicePreview = ({ channel, title, body, t, tCamp }) => {
  const raw = channel || "";
  const c = raw.toLowerCase();
  const bodyText = stripHtml(body || "");
  if (c === "email") {
    return (
      <EmailCampaignStylePreview
        title={title}
        bodyText={bodyText}
        tCamp={tCamp}
        t={t}
      />
    );
  }
  if (c === "sms") {
    return <SmsDevicePreview title={title} bodyText={bodyText} t={t} />;
  }
  if (c === "notification") {
    return (
      <NotificationDevicePreview title={title} bodyText={bodyText} t={t} />
    );
  }
  return (
    <div className="w-full max-w-[280px] rounded-lg border border-gray-700 bg-gray-800/50 p-4">
      <p className="text-center text-xs text-gray-400">
        {bodyText || stripHtml(body) || "—"}
      </p>
    </div>
  );
};

const CartRecoveryDetailPage = ({ initialRule = null }) => {
  const { t } = useTranslation("cartRecovery");
  const { t: tCamp } = useTranslation("campaigns");
  const { t: tCommon } = useTranslation("common");
  const toast = useToast();
  const { id } = useParams();
  const ruleId = parseInt(id, 10);
  const navigate = useNavigate();
  const location = useLocation();
  const ruleFromState = location.state?.rule;
  const seedRule = initialRule || ruleFromState;

  const {
    data: ruleFetched,
    isLoading: isRuleLoading,
    isError,
    error,
    refetch: refetchRule,
  } = useCartRecoveryRuleDetail(ruleId, seedRule);

  const rule = ruleFetched ?? seedRule;

  const {
    data: channels = [],
    isLoading: isChannelsLoading,
    isError: isChannelsError,
    error: channelsError,
    refetch: refetchChannels,
  } = useCartRecoveryChannels(ruleId, Boolean(ruleId));

  const updateChannelMutation = useUpdateCartRecoveryChannel();
  const deleteRuleMutation = useDeleteCartRecoveryRule();
  const [channelToggleId, setChannelToggleId] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const visibleChannels = useMemo(
    () =>
      channels.filter(
        (c) => (c.channel || "").toLowerCase() !== "notification",
      ),
    [channels],
  );

  const [selectedChannelId, setSelectedChannelId] = useState(null);

  useEffect(() => {
    if (visibleChannels.length === 0) {
      setSelectedChannelId(null);
      return;
    }
    setSelectedChannelId((prev) => {
      if (prev != null && visibleChannels.some((c) => c.id === prev)) {
        return prev;
      }
      return visibleChannels[0].id;
    });
  }, [visibleChannels]);

  const selectedChannel = useMemo(() => {
    if (!visibleChannels.length) return null;
    return (
      visibleChannels.find((c) => c.id === selectedChannelId) ||
      visibleChannels[0] ||
      null
    );
  }, [visibleChannels, selectedChannelId]);

  const handleBack = () => {
    navigate("/growth/cart-recovery");
  };

  const handleConfirmDeleteRule = async () => {
    if (!ruleId) return;
    try {
      await deleteRuleMutation.mutateAsync(ruleId);
      handleApiSuccess(t("detail.toasts.deleteSuccess"), toast, {
        title: tCommon("states.success"),
      });
      setIsDeleteModalOpen(false);
      navigate("/growth/cart-recovery");
    } catch (err) {
      handleApiError(err, toast, {
        title: tCommon("states.error"),
        defaultMessage: t("detail.toasts.deleteError"),
      });
    }
  };

  const handleChannelEnabledChange = async (ch, checked) => {
    if (!ruleId || ch?.id == null) return;
    const prevEnabled = ch.enabled !== false;
    if (checked === prevEnabled) return;
    setChannelToggleId(ch.id);
    try {
      await updateChannelMutation.mutateAsync({
        ruleId,
        channelId: ch.id,
        data: buildChannelUpdatePayload(ch, checked),
      });
      await refetchChannels();
      handleApiSuccess(t("detail.channels.toasts.updateSuccess"), toast, {
        title: tCommon("states.success"),
      });
    } catch (error) {
      handleApiError(error, toast, {
        title: tCommon("states.error"),
        defaultMessage: t("detail.channels.toasts.updateError"),
      });
    } finally {
      setChannelToggleId(null);
    }
  };

  const getLaunchTypeLabel = (launchType) => {
    if (launchType === "date") return t("launchType.date");
    return t("launchType.times");
  };

  if (isRuleLoading && !seedRule) {
    return (
      <div
        className="ordering-cart-recovery-surface flex min-h-96 items-center justify-center p-4"
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <div className="border-primary mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 sm:mb-4 sm:h-12 sm:w-12" />
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("detail.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError && !rule) {
    return (
      <div
        className="ordering-cart-recovery-surface px-4 py-8 text-center sm:py-12"
        role="alert"
      >
        <Icon
          name="ShoppingCart"
          className="text-muted-foreground mx-auto mb-3 h-8 w-8 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="text-muted-foreground mt-2 text-sm font-medium sm:text-base">
          {t("detail.error.title")}
        </h3>
        <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
          {error?.message || t("detail.error.subtitle")}
        </p>
        <Button
          emphasis="solid"
          intent="brand"
          size="touch"
          type="button"
          onClick={handleBack}
          className="mt-3 px-3 py-2 sm:mt-4 sm:px-4"
        >
          {t("detail.actions.backToList")}
        </Button>
      </div>
    );
  }

  if (!rule) {
    return null;
  }

  const ruleTitle = rule.name?.trim() || t("table.fallback.untitled");

  if (isEditing) {
    return (
      <div
        className="ordering-cart-recovery-surface space-y-6"
        data-ordering-expression="product"
      >
        <div className="flex flex-wrap items-start gap-4 [&>div]:min-w-0 [&>div]:flex-1">
          <Button
            emphasis="ghost"
            intent="neutral"
            size="iconTouch"
            type="button"
            onClick={() => setIsEditing(false)}
            className="shrink-0"
            aria-label={t("detail.actions.cancelEdit")}
          >
            <Icon name="ArrowLeft" className="text-muted-foreground h-5 w-5" />
          </Button>
          <PageHeader title={t("detail.actions.edit")} subtitle={ruleTitle} />
        </div>
        <CartRecoveryForm
          mode="edit"
          ruleId={ruleId}
          initialRule={rule}
          initialChannels={channels}
          onCancel={() => setIsEditing(false)}
          onSuccess={async () => {
            setIsEditing(false);
            await refetchRule();
            await refetchChannels();
          }}
        />
      </div>
    );
  }

  const delayMinutes = getPrimaryDelayMinutes(rule);
  const delayLabel =
    delayMinutes != null
      ? t("delay.minutesShort", { count: delayMinutes })
      : "—";

  const selectedTitle = selectedChannel?.title || "";
  const selectedBody = selectedChannel?.body || "";
  const selectedBodyText = stripHtml(selectedBody);

  return (
    <div
      className="ordering-cart-recovery-surface space-y-6"
      data-ordering-expression="product"
    >
      <div className="flex flex-wrap items-start gap-4">
        <Button
          emphasis="ghost"
          intent="neutral"
          size="iconTouch"
          type="button"
          onClick={handleBack}
          className="shrink-0"
          aria-label={t("detail.actions.backToList")}
        >
          <Icon name="ArrowLeft" className="text-muted-foreground h-5 w-5" />
        </Button>
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-foreground text-xl font-bold sm:text-2xl">
                {ruleTitle}
              </h1>
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  rule.enabled
                    ? "border-success/40 text-success border bg-[var(--success-bg)]"
                    : "border-border/40 bg-muted/10 text-muted-foreground border"
                }`}
              >
                {rule.enabled ? t("status.enabled") : t("status.disabled")}
              </span>
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              {t("detail.ruleWithId", { id: rule.id })}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={isChannelsLoading}
              className="border-border bg-card hover:bg-card text-foreground inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-[length:var(--font-size-product-ui)] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon name="Pencil" className="h-4 w-4" aria-hidden="true" />
              {t("detail.actions.edit")}
            </button>
            <button
              type="button"
              onClick={() => setIsDeleteModalOpen(true)}
              disabled={isChannelsLoading || deleteRuleMutation.isPending}
              aria-busy={deleteRuleMutation.isPending}
              className="border-destructive/40 text-destructive inline-flex items-center gap-2 rounded-lg border bg-[var(--error-bg)] px-4 py-2 text-[length:var(--font-size-product-ui)] font-medium transition-colors hover:bg-[var(--error-bg)] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={t("detail.actions.deleteAria")}
            >
              <Icon name="Trash2" className="h-4 w-4" aria-hidden="true" />
              {t("detail.actions.delete")}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="border-border bg-background rounded-xl border">
            <div className="border-border border-b p-4">
              <div className="flex items-center gap-2">
                <Icon name="BarChart3" className="text-primary h-5 w-5" />
                <h2 className="text-foreground text-base font-semibold">
                  {t("detail.configuration.title")}
                </h2>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.configuration.type")}
                  </p>
                  <span className="text-foreground capitalize">
                    {(rule.type || "—").replace(/_/g, " ")}
                  </span>
                </div>
                <div>
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.configuration.launchType")}
                  </p>
                  <span className="text-foreground">
                    {getLaunchTypeLabel(rule.launch_type)}
                  </span>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.configuration.delay")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon
                      name="Clock"
                      className="text-muted-foreground h-4 w-4"
                    />
                    <span className="text-foreground">{delayLabel}</span>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.configuration.defaultMessage")}
                  </p>
                  <div className="bg-card rounded-lg p-4">
                    <p className="text-muted-foreground text-sm whitespace-pre-wrap">
                      {rule.description || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-border bg-background rounded-xl border">
            <div className="border-border border-b p-4">
              <div className="flex items-center gap-2">
                <Icon name="Mail" className="text-primary h-5 w-5" />
                <h2 className="text-foreground text-base font-semibold">
                  {t("detail.channels.title")}
                </h2>
              </div>
            </div>
            <div className="p-4">
              {isChannelsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
                </div>
              ) : isChannelsError ? (
                <div className="text-center">
                  <p className="text-muted-foreground text-sm">
                    {channelsError?.message || t("detail.error.subtitle")}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchChannels()}
                    className="bg-card hover:bg-card text-foreground mt-3 rounded-lg px-3 py-2 text-[length:var(--font-size-product-ui)]"
                  >
                    {tCommon("actions.retry")}
                  </button>
                </div>
              ) : visibleChannels.length === 0 ? (
                <div className="py-6 text-center">
                  <Icon
                    name="FileText"
                    className="text-muted-foreground mx-auto mb-2 h-10 w-10"
                  />
                  <p className="text-muted-foreground text-sm">
                    {t("detail.channels.empty")}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                      {t("detail.channelPicker.label")}
                    </p>
                    <div
                      className="flex flex-wrap gap-2"
                      role="tablist"
                      aria-label={t("detail.channelPicker.label")}
                    >
                      {visibleChannels.map((ch) => {
                        const iconName = getChannelIconName(ch.channel);
                        const isSelected = selectedChannel?.id === ch.id;
                        const channelOn = ch.enabled !== false;
                        return (
                          <div
                            key={ch.id}
                            className={`inline-flex max-w-full items-center gap-0 rounded-lg border text-sm transition-colors ${
                              isSelected
                                ? "border-primary/50 bg-accent/15 text-white"
                                : "border-border bg-card/80 text-muted-foreground"
                            }`}
                          >
                            <button
                              type="button"
                              role="tab"
                              aria-selected={isSelected}
                              onClick={() => setSelectedChannelId(ch.id)}
                              className="inline-flex min-w-0 flex-1 items-center gap-2 rounded-l-lg px-3 py-2 text-left transition-colors hover:bg-white/5"
                            >
                              <Icon
                                name={iconName}
                                className="text-primary h-4 w-4 flex-shrink-0"
                              />
                              <span className="capitalize">{ch.channel}</span>
                              <span
                                className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] ${
                                  channelOn
                                    ? "text-success bg-[var(--success-bg)]"
                                    : "bg-muted/40 text-muted-foreground"
                                }`}
                              >
                                {channelOn
                                  ? t("detail.channels.channelEnabled")
                                  : t("detail.channels.channelDisabled")}
                              </span>
                            </button>
                            <div
                              className="border-border/80 flex shrink-0 items-center border-l px-2 py-1"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <Switch
                                checked={channelOn}
                                disabled={channelToggleId === ch.id}
                                onCheckedChange={(next) =>
                                  handleChannelEnabledChange(ch, next)
                                }
                                aria-label={t(
                                  "detail.channels.toggleChannelAria",
                                  { channel: ch.channel },
                                )}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {selectedChannel ? (
                    <>
                      <div>
                        <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                          {tCamp("detail.content.subjectLine")}
                        </p>
                        <p className="text-foreground">
                          {selectedTitle || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                          {tCamp("detail.content.bodyText")}
                        </p>
                        {selectedChannel.channel?.toLowerCase() === "email" &&
                        selectedBody?.includes("<") ? (
                          <div className="bg-card overflow-hidden rounded-lg p-4">
                            <iframe
                              title={tCamp("detail.content.bodyText")}
                              sandbox=""
                              srcDoc={selectedBody}
                              className="h-72 w-full rounded-md border-0 bg-white"
                            />
                          </div>
                        ) : (
                          <div className="bg-card overflow-hidden rounded-lg p-4">
                            <p className="overflow-wrap-anywhere text-muted-foreground font-mono text-sm break-words whitespace-pre-wrap">
                              {selectedBodyText || "—"}
                            </p>
                          </div>
                        )}
                      </div>
                      {selectedChannel.webhook ? (
                        <div>
                          <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                            {t("detail.channels.webhook")}
                          </p>
                          <p className="text-muted-foreground text-sm break-all">
                            {selectedChannel.webhook}
                          </p>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="xl:col-span-1">
          <div className="border-border bg-background rounded-xl border xl:sticky xl:top-6">
            <div className="border-border border-b p-4">
              <div className="flex items-center gap-2">
                <Icon
                  name="Smartphone"
                  className="text-muted-foreground h-5 w-5"
                />
                <h2 className="text-foreground text-base font-semibold">
                  {tCamp("detail.preview.title")}
                </h2>
              </div>
            </div>
            <div className="flex justify-center p-4">
              {isChannelsLoading ? (
                <div className="flex h-64 items-center justify-center">
                  <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
                </div>
              ) : selectedChannel ? (
                <SelectedChannelDevicePreview
                  channel={selectedChannel.channel}
                  title={selectedTitle}
                  body={selectedBody}
                  t={t}
                  tCamp={tCamp}
                />
              ) : (
                <div className="text-muted-foreground py-8 text-center text-sm">
                  {t("detail.channels.empty")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDeleteRule}
        itemName={ruleTitle}
        itemType={t("detail.deleteModal.itemType")}
        title={t("detail.deleteModal.title")}
        subtitle={t("detail.deleteModal.subtitle")}
        confirmButtonText={t("detail.deleteModal.confirm")}
        isLoading={deleteRuleMutation.isPending}
      />
    </div>
  );
};

export default CartRecoveryDetailPage;
